'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import type { HostedFields } from 'braintree-web';
import type { HostedFieldsState } from 'braintree-web/hosted-fields';

// =============================================================================
// Types
// =============================================================================

export type HostedFieldName = 'number' | 'expirationDate' | 'cvv';

export interface FieldState {
  isEmpty: boolean;
  isFocused: boolean;
  isValid: boolean;
  isPotentiallyValid: boolean;
}

export type FieldStates = Record<HostedFieldName, FieldState>;

export type BraintreeStatus = 'idle' | 'initializing' | 'ready' | 'tokenizing' | 'error';

export interface BraintreeHookError {
  code: string;
  message: string;
  userMessage: string;
  recoverable: boolean;
}

export interface HostedFieldContainerIds {
  number: string;
  expirationDate: string;
  cvv: string;
}

export interface UseBraintreeOptions {
  containerIds: HostedFieldContainerIds;
  onReady?: () => void;
  onError?: (error: BraintreeHookError) => void;
  onCardTypeChange?: (cardType: string | null) => void;
  onValidityChange?: (fieldName: HostedFieldName, isValid: boolean) => void;
  onFocusChange?: (fieldName: HostedFieldName, isFocused: boolean) => void;
}

export interface TokenizeResult {
  nonce: string;
  cardDetails: {
    bin: string;
    lastFour: string;
    cardType: string;
    expirationMonth: string;
    expirationYear: string;
  };
}

export interface UseBraintreeReturn {
  status: BraintreeStatus;
  isReady: boolean;
  isLoading: boolean;
  error: BraintreeHookError | null;
  fieldStates: FieldStates;
  cardType: string | null;
  canTokenize: boolean;
  tokenize: () => Promise<TokenizeResult>;
  focusField: (field: HostedFieldName) => void;
  clearField: (field: HostedFieldName) => void;
  retry: () => void;
}

// =============================================================================
// Constants
// =============================================================================

const INIT_TIMEOUT = 15000;
const MAX_RETRIES = 2;

const initialFieldState: FieldState = {
  isEmpty: true,
  isFocused: false,
  isValid: false,
  isPotentiallyValid: true,
};

const initialFieldStates: FieldStates = {
  number: { ...initialFieldState },
  expirationDate: { ...initialFieldState },
  cvv: { ...initialFieldState },
};

// Braintree Hosted Fields styles (applied inside iframes)
const hostedFieldStyles = {
  input: {
    'font-size': '16px',
    'font-family': 'Inter, ui-sans-serif, system-ui, sans-serif',
    color: '#2e3033',
    'line-height': '1.5',
  },
  'input.invalid': {
    color: '#ef4444',
  },
  'input.valid': {
    color: '#2e3033',
  },
  ':focus': {
    color: '#2e3033',
  },
  '::placeholder': {
    color: '#64748b',
  },
};

// =============================================================================
// Error Mapping
// =============================================================================

const userMessages: Record<string, string> = {
  HOSTED_FIELDS_FIELDS_EMPTY: 'Please fill in all payment fields.',
  HOSTED_FIELDS_FIELDS_INVALID: 'Please check your card details and try again.',
  HOSTED_FIELDS_TOKENIZATION_FAIL_ON_DUPLICATE: 'This card is already on file.',
  HOSTED_FIELDS_TOKENIZATION_CVV_VERIFICATION_FAILED:
    'CVV verification failed. Please check your security code.',
  HOSTED_FIELDS_FAILED_TOKENIZATION:
    'Unable to process your card. Please try again or use a different card.',
  HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR:
    'Connection error. Please check your internet and try again.',
  CLIENT_AUTHORIZATION_INVALID: 'Payment system configuration error. Please try again later.',
  CLIENT_GATEWAY_NETWORK: 'Unable to connect to payment system. Please try again.',
  CLIENT_REQUEST_TIMEOUT: 'Request timed out. Please try again.',
  HOSTED_FIELDS_INVALID_FIELD_SELECTOR:
    'Payment form could not be loaded. Please refresh the page.',
};

const recoverableCodes = new Set([
  'HOSTED_FIELDS_FIELDS_EMPTY',
  'HOSTED_FIELDS_FIELDS_INVALID',
  'HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR',
  'CLIENT_GATEWAY_NETWORK',
  'CLIENT_REQUEST_TIMEOUT',
]);

function mapBraintreeError(err: unknown): BraintreeHookError {
  // Handle timeout/abort errors
  if (err instanceof Error) {
    if (err.message.includes('timeout') || err.name === 'AbortError') {
      return {
        code: 'CLIENT_REQUEST_TIMEOUT',
        message: err.message,
        userMessage: userMessages['CLIENT_REQUEST_TIMEOUT'],
        recoverable: true,
      };
    }
    if (err.message.includes('network') || err.message.includes('fetch')) {
      return {
        code: 'CLIENT_GATEWAY_NETWORK',
        message: err.message,
        userMessage: userMessages['CLIENT_GATEWAY_NETWORK'],
        recoverable: true,
      };
    }
  }

  // Handle Braintree SDK errors
  const btErr = err as { code?: string; message?: string };
  if (btErr.code) {
    return {
      code: btErr.code,
      message: btErr.message || btErr.code,
      userMessage: userMessages[btErr.code] || 'An error occurred. Please try again.',
      recoverable: recoverableCodes.has(btErr.code),
    };
  }

  // Fallback for unknown errors
  return {
    code: 'UNKNOWN_ERROR',
    message: err instanceof Error ? err.message : 'Unknown error',
    userMessage: 'An unexpected error occurred. Please try again.',
    recoverable: true,
  };
}

// =============================================================================
// Helper Functions
// =============================================================================

function validateContainers(containerIds: HostedFieldContainerIds): string[] {
  const missing: string[] = [];
  for (const [field, id] of Object.entries(containerIds)) {
    if (!document.getElementById(id)) {
      missing.push(`${field} (#${id})`);
    }
  }
  return missing;
}

function mapBraintreeStateToFieldStates(state: HostedFieldsState): FieldStates {
  const result = { ...initialFieldStates };
  for (const [key, fieldData] of Object.entries(state.fields)) {
    const fieldName = key as HostedFieldName;
    if (result[fieldName]) {
      result[fieldName] = {
        isEmpty: fieldData.isEmpty,
        isFocused: fieldData.isFocused,
        isValid: fieldData.isValid,
        isPotentiallyValid: fieldData.isPotentiallyValid,
      };
    }
  }
  return result;
}

// =============================================================================
// Hook
// =============================================================================

export function useBraintree(options: UseBraintreeOptions): UseBraintreeReturn {
  const [status, setStatus] = useState<BraintreeStatus>('idle');
  const [error, setError] = useState<BraintreeHookError | null>(null);
  const [fieldStates, setFieldStates] = useState<FieldStates>(initialFieldStates);
  const [cardType, setCardType] = useState<string | null>(null);

  const hostedFieldsRef = useRef<HostedFields | null>(null);
  const mountedRef = useRef(true);
  const initAttemptRef = useRef(0);
  const initializingRef = useRef(false);
  const optionsRef = useRef(options);

  // Keep options ref updated
  optionsRef.current = options;

  // Computed values
  const isReady = status === 'ready';
  const isLoading = status === 'initializing' || status === 'tokenizing';
  const canTokenize =
    isReady &&
    fieldStates.number.isValid &&
    fieldStates.expirationDate.isValid &&
    fieldStates.cvv.isValid;

  // Initialize Braintree
  const initialize = useCallback(async () => {
    if (status === 'initializing') return;

    const tokenizationKey = process.env.NEXT_PUBLIC_BRAINTREE_TOKENIZATION_KEY;
    if (!tokenizationKey) {
      const configError: BraintreeHookError = {
        code: 'CLIENT_AUTHORIZATION_INVALID',
        message: 'Braintree tokenization key not configured',
        userMessage: userMessages['CLIENT_AUTHORIZATION_INVALID'],
        recoverable: false,
      };
      setError(configError);
      setStatus('error');
      optionsRef.current.onError?.(configError);
      return;
    }

    // Validate DOM containers exist
    const missingContainers = validateContainers(optionsRef.current.containerIds);
    if (missingContainers.length > 0) {
      const domError: BraintreeHookError = {
        code: 'HOSTED_FIELDS_INVALID_FIELD_SELECTOR',
        message: `Missing DOM containers: ${missingContainers.join(', ')}`,
        userMessage: userMessages['HOSTED_FIELDS_INVALID_FIELD_SELECTOR'],
        recoverable: true,
      };
      setError(domError);
      setStatus('error');
      optionsRef.current.onError?.(domError);
      return;
    }

    setStatus('initializing');
    setError(null);
    initializingRef.current = true;

    // Create timeout
    const timeoutId = setTimeout(() => {
      if (mountedRef.current && initializingRef.current) {
        initializingRef.current = false;
        const timeoutError = mapBraintreeError(new Error('Initialization timed out'));
        setError(timeoutError);
        setStatus('error');
        optionsRef.current.onError?.(timeoutError);
      }
    }, INIT_TIMEOUT);

    try {
      // Dynamic import to avoid SSR issues
      const braintree = await import('braintree-web');

      if (!mountedRef.current) {
        clearTimeout(timeoutId);
        return;
      }

      // Create hosted fields instance
      const hostedFieldsInstance = await braintree.hostedFields.create({
        authorization: tokenizationKey,
        styles: hostedFieldStyles,
        fields: {
          number: {
            container: `#${optionsRef.current.containerIds.number}`,
            // placeholder: '**** **** **** ****',
            placeholder: '•••• •••• •••• ••••',
            // Only accept Visa, Mastercard, and Amex
            supportedCardBrands: {
              visa: true,
              mastercard: true,
              'american-express': true,
              discover: false,
              jcb: false,
              'diners-club': false,
              maestro: false,
              'union-pay': false,
              elo: false,
              mir: false,
              hiper: false,
              hipercard: false,
            },
          },
          expirationDate: {
            container: `#${optionsRef.current.containerIds.expirationDate}`,
            placeholder: 'MM/YY',
          },
          cvv: {
            container: `#${optionsRef.current.containerIds.cvv}`,
            placeholder: '•••',
          },
        },
      });

      clearTimeout(timeoutId);

      if (!mountedRef.current) {
        await hostedFieldsInstance.teardown();
        return;
      }

      hostedFieldsRef.current = hostedFieldsInstance;

      // Initialize field states
      const initialState = hostedFieldsInstance.getState();
      setFieldStates(mapBraintreeStateToFieldStates(initialState));

      // Attach event listeners
      hostedFieldsInstance.on('validityChange', (event) => {
        if (!mountedRef.current) return;

        const fieldName = event.emittedBy as HostedFieldName;
        const fieldData = event.fields[event.emittedBy];

        setFieldStates((prev) => ({
          ...prev,
          [fieldName]: {
            ...prev[fieldName],
            isValid: fieldData.isValid,
            isPotentiallyValid: fieldData.isPotentiallyValid,
          },
        }));

        optionsRef.current.onValidityChange?.(fieldName, fieldData.isValid);
      });

      hostedFieldsInstance.on('cardTypeChange', (event) => {
        if (!mountedRef.current) return;

        const cards = event.cards;
        if (cards.length === 1) {
          const detectedType = cards[0].type;
          setCardType(detectedType);
          optionsRef.current.onCardTypeChange?.(detectedType);
        } else {
          setCardType(null);
          optionsRef.current.onCardTypeChange?.(null);
        }
      });

      hostedFieldsInstance.on('focus', (event) => {
        if (!mountedRef.current) return;

        const fieldName = event.emittedBy as HostedFieldName;
        setFieldStates((prev) => ({
          ...prev,
          [fieldName]: { ...prev[fieldName], isFocused: true },
        }));

        optionsRef.current.onFocusChange?.(fieldName, true);
      });

      hostedFieldsInstance.on('blur', (event) => {
        if (!mountedRef.current) return;

        const fieldName = event.emittedBy as HostedFieldName;
        setFieldStates((prev) => ({
          ...prev,
          [fieldName]: { ...prev[fieldName], isFocused: false },
        }));

        optionsRef.current.onFocusChange?.(fieldName, false);
      });

      hostedFieldsInstance.on('empty', (event) => {
        if (!mountedRef.current) return;

        const fieldName = event.emittedBy as HostedFieldName;
        setFieldStates((prev) => ({
          ...prev,
          [fieldName]: { ...prev[fieldName], isEmpty: true },
        }));
      });

      hostedFieldsInstance.on('notEmpty', (event) => {
        if (!mountedRef.current) return;

        const fieldName = event.emittedBy as HostedFieldName;
        setFieldStates((prev) => ({
          ...prev,
          [fieldName]: { ...prev[fieldName], isEmpty: false },
        }));
      });

      initializingRef.current = false;
      setStatus('ready');
      initAttemptRef.current = 0;
      optionsRef.current.onReady?.();
    } catch (err) {
      clearTimeout(timeoutId);
      initializingRef.current = false;

      if (!mountedRef.current) return;

      const shouldRetry = initAttemptRef.current < MAX_RETRIES;

      if (shouldRetry) {
        initAttemptRef.current++;
        // Exponential backoff: 500ms, 1000ms, 2000ms
        const delay = Math.pow(2, initAttemptRef.current) * 250;
        setTimeout(() => {
          if (mountedRef.current) {
            setStatus('idle'); // Reset to trigger re-init
          }
        }, delay);
      } else {
        const hookError = mapBraintreeError(err);
        setError(hookError);
        setStatus('error');
        optionsRef.current.onError?.(hookError);
      }
    }
  }, [status]);

  // Tokenize card data
  const tokenize = useCallback(async (): Promise<TokenizeResult> => {
    const instance = hostedFieldsRef.current;

    if (!instance || status !== 'ready') {
      throw {
        code: 'UNKNOWN_ERROR',
        message: 'Braintree not initialized',
        userMessage: 'Payment form not ready. Please wait and try again.',
        recoverable: true,
      } as BraintreeHookError;
    }

    setStatus('tokenizing');

    try {
      const result = await instance.tokenize();

      if (!mountedRef.current) {
        throw {
          code: 'UNKNOWN_ERROR',
          message: 'Component unmounted',
          userMessage: 'Please try again.',
          recoverable: false,
        } as BraintreeHookError;
      }

      setStatus('ready');

      return {
        nonce: result.nonce,
        cardDetails: {
          bin: result.details.bin,
          lastFour: result.details.lastFour,
          cardType: result.details.cardType,
          expirationMonth: result.details.expirationMonth,
          expirationYear: result.details.expirationYear,
        },
      };
    } catch (err) {
      if (!mountedRef.current) {
        throw {
          code: 'UNKNOWN_ERROR',
          message: 'Component unmounted',
          userMessage: 'Please try again.',
          recoverable: false,
        } as BraintreeHookError;
      }

      setStatus('ready');
      throw mapBraintreeError(err);
    }
  }, [status]);

  // Focus a specific field
  const focusField = useCallback((field: HostedFieldName) => {
    hostedFieldsRef.current?.focus(field);
  }, []);

  // Clear a specific field
  const clearField = useCallback((field: HostedFieldName) => {
    hostedFieldsRef.current?.clear(field);
  }, []);

  // Retry initialization
  const retry = useCallback(() => {
    if (status === 'error') {
      initAttemptRef.current = 0;
      setError(null);
      setStatus('idle');
    }
  }, [status]);

  // Initialize on mount (with small delay for DOM readiness)
  useEffect(() => {
    const initTimer = setTimeout(() => {
      if (mountedRef.current && status === 'idle') {
        initialize();
      }
    }, 50);

    return () => {
      clearTimeout(initTimer);
    };
  }, [initialize, status]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      const instance = hostedFieldsRef.current;
      if (instance) {
        instance.teardown().catch((err) => {
          console.warn('Braintree teardown error:', err);
        });
        hostedFieldsRef.current = null;
      }
    };
  }, []);

  return {
    status,
    isReady,
    isLoading,
    error,
    fieldStates,
    cardType,
    canTokenize,
    tokenize,
    focusField,
    clearField,
    retry,
  };
}
